import './style.css';
import { useMemo, useEffect, useState, useRef, useContext } from 'react';
import queryString from 'query-string';
import { useCalculator } from './hooks/use-calculator';
import { useLoanSubmit } from './hooks/use-loan-submit';
import {
    applicationResponseTypeDict,
    BrandedLoanCalculatorProps,
    FieldValuesType,
    LabelsType,
    scopeToProductVariant,
    StepCommonProps,
    SubmitApplicationOk,
} from './types';
import { getLabel } from './labels';
import { ProgressBar } from '../../progress-bar/progress-bar';
import { SESSION_STORAGE_STEP_KEY, useValues } from './hooks/use-values';
import { Intro } from './components/steps/intro';
import { StepOne } from './components/steps/step-one';
import { StepTwo } from './components/steps/step-two';
import { StepThree } from './components/steps/step-three';
import { ApprovedApplication } from './components/approved-application';
import { RejectedApplication } from './components/rejected-application';
import { ManualApplication } from './components/manual-application';
import { SubmitErrorApplication } from './components/submit-error-application';

import { mockService, MockSubmitType } from './mock-service';
import { AppContext } from '@/contexts/app-context';
import { ConsumerApplicationProduct, GenericEvent, useDataLayer } from '@/hooks/use-data-layer';
import { useLogin } from '@/hooks/use-login';

export type CalculatorProps = ReturnType<typeof useCalculator>;
export type CalculatorStateProps = CalculatorProps['calculatorState'];

export type LinkType = {
    url: string;
    text?: string;
    target?: string;
    rel?: string;
};

export type StepIntroProps = StepCommonProps & {
    calculator: CalculatorProps & {
        loanAmountMin: number;
        loanAmountMax: number;
        loanAmountStep?: number;
        tenorYearsMin: number;
        tenorYearsMax: number;
    };
    common: {
        labels: LabelsType;
        commonTermsUrl: LinkType;
        secciUrl: LinkType;
        props: BrandedLoanCalculatorProps;
    };
    login: (ssn: string) => void;
};

export const BrandedConsumerLoan = (props: BrandedLoanCalculatorProps) => {
    const {
        labels,
        loanAmountMin,
        loanAmountMax,
        loanAmountDefault,
        loanAmountStep,
        tenorYearsMin,
        tenorYearsMax,
        tenorYearsDefault,
        commonTermsUrl,
        secciUrl,
        productType,
        insuranceLinks,
        fixedRate,
        administrativeFee = 0,
        loanType = '',
        scope = 'carla_se',
        brandColor = '#117069',
        chooseLoanType = false,
        allowCoapplicant = false,
        skipBankIdLogin = false,
    } = props;
    const context = useContext(AppContext);
    const { calculatorState, updateState, monthlyAmount, formatNumber } = useCalculator({
        loanAmountDefault,
        tenorYearsDefault,
        productType,
        fixedRate,
        administrativeFee,
    });
    const { dataLayerPush } = useDataLayer();
    const { getLoginHintQuery } = useLogin();
    const { submit } = useLoanSubmit(scope);
    const { values, setValues, step, setStep, getValuesFromStorage, resetAll, decisionResult, setDecisionResult } = useValues(loanType);

    const containerRef = useRef<HTMLDivElement>(null);

    const [showError, setShowError] = useState(false);
    const [dataLayerEvents, setDataLayerEvents] = useState<string[]>([]);

    const currentStep = useMemo(() => (decisionResult ? 4 : step), [decisionResult, step]);
    const shouldShowProgressBar = useMemo(() => {
        const isAuthorized = skipBankIdLogin || context?.user?.isAuthenticated;
        const isInProgressSteps = step >= 1 && step <= 3;
        return isAuthorized && !showError && (isInProgressSteps || decisionResult);
    }, [skipBankIdLogin, context, showError, step, decisionResult]);

    const getLabelCallback = (key: keyof LabelsType, replacements?: Record<string, string | number>) => getLabel(labels, key, replacements);

    const stepTitles: string[] = [
        getLabelCallback('formStepOneTitle'),
        getLabelCallback('formStepTwoTitle'),
        getLabelCallback('formStepThreeTitle'),
        getLabelCallback('formStepResultTitle'),
    ];

    const DATALAYER_DEFAULTS = {
        Product: ConsumerApplicationProduct['BRANDED_CONSUMER_LOAN'],
        ProductVariant: scopeToProductVariant[scope],
    };

    useEffect(() => {
        const event: GenericEvent = {
            ...DATALAYER_DEFAULTS,
            event: step === 0 ? 'ApplicationStart' : 'ApplicationProgress',
            StepNumber: `${step}`,
            StepName: step === 0 ? 'Calculator' : (stepTitles[step - 1] ?? ''),
        };

        if (!dataLayerEvents.includes(`${step}`)) {
            dataLayerPush(event);
            setDataLayerEvents([...dataLayerEvents, `${step}`]);
        }
    }, [step]);

    const scrollToTop = () => {
        if (containerRef?.current) {
            window.scrollTo({
                behavior: 'smooth',
                top: containerRef.current.getBoundingClientRect().top - document.body.getBoundingClientRect().top - 250,
            });
        }
    };

    const focusInFirstInvalid = () => {
        if (!containerRef.current) return;

        const firstInvalid = containerRef.current.querySelector('[aria-invalid="true"]');
        if (firstInvalid instanceof HTMLElement) {
            // For inputs/textareas, focus directly. For other elements (like combobox), find focusable child
            if (firstInvalid.tagName === 'INPUT' || firstInvalid.tagName === 'TEXTAREA') {
                firstInvalid.focus();
            } else {
                const focusable = firstInvalid.querySelector<HTMLElement>('input, textarea, [tabindex="0"]');
                if (focusable) {
                    focusable.focus();
                }
            }
        }
    };

    useEffect(() => {
        if (step > 0) {
            scrollToTop();
        }
    }, [step, decisionResult, showError]);

    useEffect(() => {
        if (!containerRef?.current) {
            return;
        }

        containerRef.current.querySelectorAll('a').forEach(link => {
            try {
                const url = new URL(link.href);
                if (window?.location?.hostname !== url.hostname) {
                    link.classList.add('external-link');
                }
            } catch {
                // Ignore invalid URLs
            }
        });
    }, [containerRef, step]);

    const handleLogin = async (ssn: string | number) => {
        sessionStorage.setItem(SESSION_STORAGE_STEP_KEY, '1');

        if (skipBankIdLogin) {
            setStep(1);
            return;
        }

        const { loginHint } = await getLoginHintQuery(ssn);
        if (!loginHint) {
            console.error('Invalid login hint');
        }

        const queryParams: Record<string, string> = {
            scope: 'carlaScope',
            customRedirectUrl: `${context.url}${location.pathname}`,
        };

        if (loginHint) {
            queryParams.loginHint = loginHint;
        }

        window.location.href = `/login?${queryString.stringify(queryParams)}`;
    };

    const onSubmit = async (mockType?: MockSubmitType) => {
        try {
            dataLayerPush({
                ...DATALAYER_DEFAULTS,
                event: 'ApplicationSubmitted',
            });

            let response: SubmitApplicationOk;
            if (mockType) {
                response = await mockService.submit(mockType);
            } else {
                response = await submit(values, calculatorState);
            }

            const { decision, applicationReference } = response.result.submitApplicationExtResult;

            dataLayerPush({
                ...DATALAYER_DEFAULTS,
                event: 'ApplicationStatus',
                ApplicationID: applicationReference || '',
                Status: applicationResponseTypeDict[decision] || 'Unknown',
            });

            setDecisionResult(response.result.submitApplicationExtResult);
        } catch {
            setShowError(true);
        }
    };

    const progressLabels = useMemo(() => {
        if (decisionResult) {
            return {
                current: stepTitles[3],
                next: '',
            };
        }
        const current = stepTitles[step - 1] ?? '';
        const next = stepTitles[step] ?? '';
        return {
            current,
            next,
        };
    }, [step, stepTitles, decisionResult]);

    const renderStepContent = () => {
        const commonProps: StepCommonProps = {
            step,
            setStep: setStep,
            setValues: (values: FieldValuesType) => setValues(values),
            fields: values,
            getValuesFromStorage,
            updateFields: (updatedValues: Partial<FieldValuesType>) =>
                setValues((previousValues: FieldValuesType) => ({ ...previousValues, ...updatedValues })),

            getLabel: getLabelCallback,
            brandColor,
            scrollToTop,
            allowCoapplicant,
            chooseLoanType,
            skipBankIdLogin,
            focusInFirstInvalid,
        };

        if (decisionResult?.decision === 'APPROVED') {
            return <ApprovedApplication {...commonProps} result={decisionResult} onFinish={resetAll} />;
        }
        if (decisionResult?.decision === 'REJECTED') {
            return <RejectedApplication {...commonProps} onFinish={resetAll} />;
        }
        if (decisionResult?.decision === 'MANUAL_INSPECTION') {
            return <ManualApplication {...commonProps} onFinish={resetAll} />;
        }

        if (showError || (!skipBankIdLogin && !context?.user?.isAuthenticated && step > 0)) {
            return <SubmitErrorApplication {...commonProps} />;
        }

        switch (step) {
            case 0:
                return (
                    <Intro
                        calculator={{
                            calculatorState,
                            updateState,
                            monthlyAmount,
                            formatNumber,
                            loanAmountMin,
                            loanAmountMax,
                            loanAmountStep,
                            tenorYearsMin,
                            tenorYearsMax,
                        }}
                        common={{
                            labels,
                            commonTermsUrl,
                            secciUrl,
                            props,
                        }}
                        login={handleLogin}
                        {...commonProps}
                    />
                );
            case 1:
                return <StepOne {...commonProps} />;
            case 2:
                return <StepTwo {...commonProps} insuranceLinks={insuranceLinks} />;
            case 3:
                return <StepThree {...commonProps} calculator={calculatorState} onSubmit={onSubmit} />;
        }
    };

    return (
        <div data-component="branded-consumer-loan" className="r-container r-pb-8" ref={containerRef}>
            <div className="r-relative r-mx-auto r-max-w-2xl">
                {shouldShowProgressBar && (
                    <div className="r-mb-8">
                        <ProgressBar
                            totalSteps={4}
                            currentStep={currentStep}
                            currentLabel={progressLabels.current}
                            nextLabel={progressLabels.next}
                            brandColor={brandColor}
                        />
                    </div>
                )}
            </div>
            {renderStepContent()}
        </div>
    );
};
